import { Link } from "react-router-dom";
import { BookMarked, FileText, ArrowLeft } from "lucide-react";

const LAST_UPDATED = "13 February 2026";
const CONTACT_EMAIL = "contact@studictsmartdictionary.com";

export default function Terms() {
  return (
    <div className="min-h-screen bg-yellow-50">
      <header className="px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between border-b-2 border-slate-900 bg-white">
        <Link to="/" data-testid="terms-back" className="font-display text-xl sm:text-2xl font-black flex items-center gap-2">
          <BookMarked className="w-6 h-6" strokeWidth={2.5}/> Studict
        </Link>
        <Link to="/" className="text-xs sm:text-sm font-bold inline-flex items-center gap-1 underline hover:no-underline">
          <ArrowLeft className="w-3 h-3" strokeWidth={2.5}/> Home
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="terms-page">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-7 h-7" strokeWidth={2.5}/>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Terms of Service</p>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight">The fair-use deal</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-slate max-w-none mt-8 prose-headings:font-black prose-headings:font-display prose-h2:text-2xl prose-h2:mt-10">

          <h2>1. Acceptance</h2>
          <p>
            By creating an account or using Studict, you agree to these Terms of Service and our
            <Link to="/privacy"> Privacy Policy</Link>. If you don't agree, please don't use the service.
          </p>

          <h2>2. Who can use Studict</h2>
          <p>
            You must be 13 or older. Users aged 13–17 must have a parent or guardian's permission.
            One account per person. You're responsible for keeping your sign-in method secure.
          </p>

          <h2>3. Plans &amp; payment</h2>
          <ul>
            <li><strong>Free</strong>: 100 MB storage, limited AI quizzes, ads-free always.</li>
            <li><strong>Pro / Pro Plus</strong>: priced and billed in your local currency where supported.</li>
            <li>
              At checkout you can choose <strong>auto-renew</strong> (monthly, cancel anytime in
              Settings → Manage subscription) or a one-off 30-day payment.
            </li>
            <li>
              <strong>Refunds:</strong> within 14 days of your first paid purchase you can request a
              full refund by emailing us — no questions asked. After that, paid periods are
              non-refundable but you keep access until the period ends.
            </li>
            <li>
              All payments are processed by <strong>Stripe</strong>. We don't see or store your card details.
            </li>
          </ul>

          <h2>4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Upload content you don't have the right to share (copyright, IP, private data)</li>
            <li>Use Studict to harass, defraud, or harm others</li>
            <li>Try to break, scrape or reverse-engineer the service</li>
            <li>Use the AI features to generate content that is illegal, exploitative, or hateful</li>
            <li>Resell or wholesale Studict access without our written permission</li>
          </ul>

          <h2>5. Your content</h2>
          <p>
            You keep all rights to the glossary entries, materials, and notes you upload. By using
            Studict you grant us a limited licence to store and process that content solely to
            provide the service to you. We don't claim ownership and we don't use it to train AI.
          </p>

          <h2>6. AI-generated content</h2>
          <p>
            Quiz questions, draft assignments and OCR text are AI-generated and may contain errors.
            Always check important information against your textbook or teacher. Studict is a study
            companion — not a substitute for qualified educational guidance.
          </p>

          <h2>7. Service availability</h2>
          <p>
            We aim for high uptime but occasionally need to take the service down for maintenance.
            We don't guarantee continuous availability and we are not liable for losses caused by
            short outages.
          </p>

          <h2>8. Termination</h2>
          <p>
            You can close your account anytime from Settings → Delete my account. We may suspend or
            close accounts that breach these terms; where reasonable we'll give you a chance to fix
            the issue first.
          </p>

          <h2>9. Liability</h2>
          <p>
            To the maximum extent permitted by law, Studict's liability for any claim arising from
            the service is limited to the amount you paid us in the 12 months before the claim.
            Nothing in these terms excludes liability for death, personal injury, fraud or any
            other liability that cannot be limited under English law.
          </p>

          <h2>10. Governing law</h2>
          <p>
            These terms are governed by the laws of England and Wales. Disputes will be subject to
            the exclusive jurisdiction of the courts of England and Wales unless you have a
            statutory right to bring proceedings elsewhere.
          </p>

          <h2>11. Changes</h2>
          <p>
            We may update these terms; material changes are announced by email and on the homepage.
            Continued use after a change means you accept the new terms.
          </p>

          <h2>12. Contact</h2>
          <p>
            Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> for anything — questions,
            disputes or general feedback.
          </p>
        </div>

        <div className="mt-12 pt-6 border-t-2 border-slate-300 flex flex-wrap gap-4 text-sm font-bold">
          <Link to="/" className="underline">Home</Link>
          <Link to="/privacy" className="underline">Privacy Policy</Link>
          <Link to="/support" className="underline">Contact support</Link>
        </div>
      </main>
    </div>
  );
}
